import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebaseConfig';

const collectionName = 'crm_clients';

// Get all clients
export const getClients = async () => {
  try {
    const q = query(collection(db, 'crm_clients'), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      let data = doc.data();
      data.id = doc.id;
      
      if (data.nextDueDate && data.startDate) {
        const start = new Date(data.startDate);
        if (!isNaN(start.getTime())) {
          let d1 = start.getDate();
          if (d1 > 28) d1 = 28;
          let d2 = d1 + 15;
          if (d1 > 15) d2 = d1 - 15; else if (d1 === 15) d2 = 30;
          
          const t = new Date(data.nextDueDate);
          if (!isNaN(t.getTime())) {
            const tDay = t.getDate();
            if (tDay !== d1 && tDay !== d2) {
              let b = new Date(t);
              while(b.getDate() !== d1 && b.getDate() !== d2) b.setDate(b.getDate()-1);
              let f = new Date(t);
              while(f.getDate() !== d1 && f.getDate() !== d2) f.setDate(f.getDate()+1);
              
              if (Math.abs(t-b) <= Math.abs(f-t)) {
                data.nextDueDate = b.toISOString();
              } else {
                data.nextDueDate = f.toISOString();
              }
            }
          }
        }
      }
      return data;
    });
  } catch (error) {
    console.error("Error fetching clients: ", error);
    return [];
  }
};

// Add a new client
export const addClient = async (clientData) => {
  try {
    const docRef = await addDoc(collection(db, collectionName), {
      ...clientData,
      createdAt: new Date().toISOString()
    });
    return { id: docRef.id, ...clientData };
  } catch (error) {
    console.error("Error adding client: ", error);
    throw error;
  }
};

// Update a client
export const updateClient = async (clientId, updatedData) => {
  try {
    const clientRef = doc(db, collectionName, clientId);
    await updateDoc(clientRef, updatedData);
    return true;
  } catch (error) {
    console.error("Error updating client: ", error);
    throw error;
  }
};

// Delete a client
export const deleteClient = async (clientId) => {
  try {
    const clientRef = doc(db, collectionName, clientId);
    await deleteDoc(clientRef);
    return true;
  } catch (error) {
    console.error("Error deleting client: ", error);
    throw error;
  }
};

// --- Transactions ---
export const addTransaction = async (transactionData) => {
  try {
    const docRef = await addDoc(collection(db, 'crm_transactions'), {
      date: new Date().toISOString(),
      ...transactionData
    });
    return { id: docRef.id, ...transactionData };
  } catch (error) {
    console.error("Error adding transaction: ", error);
    throw error;
  }
};

export const getTransactions = async () => {
  try {
    const q = query(collection(db, 'crm_transactions'), orderBy('date', 'desc'));
    const querySnapshot = await getDocs(q);
    const transactions = [];
    querySnapshot.forEach((doc) => {
      transactions.push({ id: doc.id, ...doc.data() });
    });
    return transactions;
  } catch (error) {
    console.error("Error fetching transactions: ", error);
    return [];
  }
};

// --- Other Revenue ---
export const addOtherRevenue = async (revenueData) => {
  try {
    const docRef = await addDoc(collection(db, 'crm_other_revenue'), {
      date: new Date().toISOString(),
      ...revenueData
    });
    return { id: docRef.id, ...revenueData };
  } catch (error) {
    console.error("Error adding other revenue: ", error);
    throw error;
  }
};

export const getOtherRevenues = async () => {
  try {
    const q = query(collection(db, 'crm_other_revenue'), orderBy('date', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching other revenue: ", error);
    return [];
  }
};

export const addExpense = async (expenseData) => {
  try {
    const docRef = await addDoc(collection(db, 'crm_expenses'), {
      date: new Date().toISOString(),
      ...expenseData
    });
    const finalDate = expenseData.date || new Date().toISOString();
    return { id: docRef.id, ...expenseData, date: finalDate };
  } catch (error) {
    console.error("Error adding expense: ", error);
    throw error;
  }
};

export const getExpenses = async () => {
  try {
    const q = query(collection(db, 'crm_expenses'), orderBy('date', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching expenses: ", error);
    return [];
  }
};

// --- Users ---
export const getUsers = async () => {
  try {
    const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching users: ", error);
    return [];
  }
};

export const addUser = async (userData) => {
  try {
    const docRef = await addDoc(collection(db, 'users'), {
      ...userData,
      createdAt: new Date().toISOString()
    });
    return { id: docRef.id, ...userData };
  } catch (error) {
    console.error("Error adding user: ", error);
    throw error;
  }
};

export const deleteUser = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    await deleteDoc(userRef);
    return true;
  } catch (error) {
    console.error("Error deleting user: ", error);
    throw error;
  }
};

// --- Personal Expenses ---
export const getPersonalExpenses = async () => {
  try {
    const q = query(collection(db, 'crm_personal_expenses'), orderBy('date', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching personal expenses: ", error);
    return [];
  }
};

export const addPersonalExpense = async (expenseData) => {
  try {
    const docRef = await addDoc(collection(db, 'crm_personal_expenses'), {
      date: new Date().toISOString(),
      ...expenseData
    });
    const finalDate = expenseData.date || new Date().toISOString();
    return { id: docRef.id, ...expenseData, date: finalDate };
  } catch (error) {
    console.error("Error adding personal expense: ", error);
    throw error;
  }
};

export const deletePersonalExpense = async (expenseId) => {
  try {
    const expenseRef = doc(db, 'crm_personal_expenses', expenseId);
    await deleteDoc(expenseRef);
    return true;
  } catch (error) {
    console.error("Error deleting personal expense: ", error);
    throw error;
  }
};

// --- Callback Requests ---
export const addCallbackRequest = async (requestData) => {
  try {
    const docRef = await addDoc(collection(db, 'callback_requests'), {
      ...requestData,
      createdAt: requestData.createdAt || new Date().toISOString(),
      status: requestData.status || 'New'
    });
    return { id: docRef.id, ...requestData };
  } catch (error) {
    console.error("Error adding callback request: ", error);
    throw error;
  }
};

export const getCallbackRequests = async () => {
  try {
    const q = query(collection(db, 'callback_requests'), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching callback requests: ", error);
    return [];
  }
};

export const updateCallbackRequest = async (id, updatedData) => {
  try {
    const reqRef = doc(db, 'callback_requests', id);
    await updateDoc(reqRef, updatedData);
    return true;
  } catch (error) {
    console.error("Error updating callback request: ", error);
    throw error;
  }
};
